import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  FolderPlus, 
  FileText, 
  DollarSign, 
  CheckCircle2, 
  Clock,
  ArrowRight,
  Calculator,
  Users,
  AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  case_setup: { label: "Setup", color: "bg-slate-100 text-slate-700" },
  scope_building: { label: "Scope", color: "bg-blue-100 text-blue-700" },
  hidden_work: { label: "Hidden Work", color: "bg-purple-100 text-purple-700" },
  compliance: { label: "Compliance", color: "bg-orange-100 text-orange-700" },
  coding: { label: "Cost Codes", color: "bg-cyan-100 text-cyan-700" },
  contract_type: { label: "Contract", color: "bg-indigo-100 text-indigo-700" },
  pricing_strategy: { label: "Pricing", color: "bg-pink-100 text-pink-700" },
  bidding: { label: "Bidding", color: "bg-yellow-100 text-yellow-700" },
  quote_intake: { label: "Quotes", color: "bg-teal-100 text-teal-700" },
  leveling: { label: "Leveling", color: "bg-lime-100 text-lime-700" },
  final: { label: "Complete", color: "bg-green-100 text-green-700" }
};

export default function Dashboard() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      setCases(response.data);
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalCases = cases.length;
  const totalEstimate = cases.reduce((sum, c) => sum + (c.total_cost || 0), 0);
  const completedCases = cases.filter(c => c.status === "final").length;
  const activeBidding = cases.filter(c => c.status === "bidding" || c.status === "leveling").length;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStepProgress = (currentStep) => {
    return Math.min(100, Math.round(((currentStep || 0) / 9) * 100));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Manage your estimate cases</p>
        </div>
        <Link to="/new-case">
          <Button className="gap-2" data-testid="new-case-btn">
            <FolderPlus className="w-4 h-4" /> New Case
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="kpi-card" data-testid="kpi-total-cases">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-primary/10 text-primary"><FileText className="w-5 h-5" /></div>
            <span className="kpi-label">Cases</span>
          </div>
          <span className="kpi-value">{totalCases}</span>
        </div>

        <div className="kpi-card" data-testid="kpi-total-estimate">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-500/10 text-green-600"><DollarSign className="w-5 h-5" /></div>
            <span className="kpi-label">Total Estimated</span>
          </div>
          <span className="kpi-value font-mono">{formatCurrency(totalEstimate)}</span>
        </div>

        <div className="kpi-card" data-testid="kpi-completed">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-500/10 text-blue-600"><CheckCircle2 className="w-5 h-5" /></div>
            <span className="kpi-label">Completed</span>
          </div>
          <span className="kpi-value">{completedCases}</span>
        </div>

        <div className="kpi-card" data-testid="kpi-bidding">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-yellow-500/10 text-yellow-600"><Users className="w-5 h-5" /></div>
            <span className="kpi-label">Active Bidding</span>
          </div>
          <span className="kpi-value">{activeBidding}</span>
        </div>
      </div>

      {/* Cases List */}
      <div>
        <h2 className="text-lg font-heading font-semibold mb-4">Estimate Cases</h2>
        
        {cases.length === 0 ? (
          <div className="swiss-card empty-state">
            <Calculator className="empty-state-icon" />
            <h3 className="text-lg font-heading font-semibold mb-2">No cases yet</h3>
            <p className="text-muted-foreground mb-6">Create your first estimate case to get started</p>
            <Link to="/new-case">
              <Button className="gap-2"><FolderPlus className="w-4 h-4" /> Create Case</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
            {cases.map((c) => {
              const statusConfig = STATUS_CONFIG[c.status] || STATUS_CONFIG.case_setup;
              const progress = getStepProgress(c.current_step);
              
              return (
                <Link
                  key={c.id}
                  to={`/case/${c.id}`}
                  className="project-card group"
                  data-testid={`case-card-${c.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                        {c.name}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate">{c.client_name}</p>
                    </div>
                    <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{c.address}</p>

                  {/* Progress */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Step {Math.min(c.current_step || 0, 9)}/9</span>
                      <span className="text-muted-foreground">{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Estimate</span>
                      <span className="font-heading font-semibold font-mono">
                        {formatCurrency(c.total_cost || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Scope Items</span>
                      <span>{(c.scope_items || []).length}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(c.updated_at).toLocaleDateString()}
                    </span>
                    <span className="text-sm text-primary font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      View <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
